/**
 * LoadingSpinner - Consistent loading spinner component
 *
 * Provides a reusable loading spinner with different size variants
 * and an optional full-screen overlay mode.
 *
 * Features:
 * - Size variants: small, medium, large
 * - Overlay mode for full-screen blocking
 * - Accessible with aria-label
 * - Optional custom message
 *
 * @module components/common/LoadingSpinner
 *
 * @example
 * import { LoadingSpinner } from '@/components/common/LoadingSpinner';
 *
 * // Basic usage
 * <LoadingSpinner />
 *
 * // With size
 * <LoadingSpinner size="large" />
 *
 * // Full-screen overlay
 * <LoadingSpinner overlay message="Cargando datos..." />
 */

import React from 'react';
import PropTypes from 'prop-types';

/**
 * Loading spinner component with size variants and overlay mode
 *
 * @param {Object} props
 * @param {('small'|'medium'|'large')} [props.size='medium'] - Spinner size
 * @param {boolean} [props.overlay=false] - Enable full-screen overlay mode
 * @param {string} [props.message] - Optional loading message to display
 * @param {string} [props.aria_label='Cargando...'] - Accessible label for screen readers
 * @returns {JSX.Element}
 */
export function LoadingSpinner({
  size = 'medium',
  overlay = false,
  message,
  aria_label = 'Cargando...',
}) {
  const size_classes = {
    small: 'spinner-small h-4 w-4 border-2',
    medium: 'spinner-medium h-8 w-8 border-2',
    large: 'spinner-large h-12 w-12 border-4',
  };

  const spinner_class = size_classes[size] || size_classes.medium;

  const spinner_element = (
    <div className="spinner-container flex flex-col items-center gap-3">
      <div
        className={`spinner animate-spin rounded-full border-raios-tertiary border-t-transparent ${spinner_class}`}
        role="status"
        aria-label={aria_label}
        aria-live="polite"
      >
        <span className="sr-only">{aria_label}</span>
      </div>

      {message && (
        <p className="spinner-message text-raios-text-support text-sm font-mono" aria-live="polite">
          {message}
        </p>
      )}
    </div>
  );

  if (overlay) {
    return (
      <div
        className="spinner-overlay fixed inset-0 z-50 flex items-center justify-center bg-raios-secondary/80 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-label={message || aria_label}
      >
        {spinner_element}
      </div>
    );
  }

  return spinner_element;
}

LoadingSpinner.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  overlay: PropTypes.bool,
  message: PropTypes.string,
  aria_label: PropTypes.string,
};

// Default export for backward compatibility
export default LoadingSpinner;
